import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  Image,
  Modal,
  Paper,
  Text,
  ThemeIcon,
  Tooltip,
  CopyButton,
  ActionIcon,
  SimpleGrid,
  Stack,
} from "@mantine/core";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import { DateField, Show } from "@refinedev/mantine";
import { Review, SOURCE_COLORS, SOURCE_LABELS } from "entities/review";
import { countryCodeToFlag } from "utils/countryFlag";
import {
  IconUser,
  IconPackage,
  IconCopy,
  IconCheck,
  IconPhoto,
  IconLink,
  IconShieldCheck,
  IconShieldOff,
  IconCalendar,
  IconExternalLink,
  IconChevronLeft,
  IconChevronRight,
  IconClipboard,
  IconZoomIn,
  IconEye,
  IconEyeOff,
} from "@tabler/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  StarRating,
  STAR_HEX,
  STAR_BG_LIGHT,
  STAR_BG_DARK,
  STAR_LABELS,
} from "./StarRating";
import { usePublishReview } from "./usePublishReview";
import { openConfirmModal } from "@mantine/modals";

/* ═══════════════════════════════════════════════════════════════════════════
   Copyable monospace value
   ═══════════════════════════════════════════════════════════════════════════ */

function CopyableText({ value }: { value: string }) {
  if (!value)
    return (
      <Text size="sm" color="dimmed">
        —
      </Text>
    );
  return (
    <Group spacing={4} noWrap>
      <Text size="sm" weight={500} sx={{ fontFamily: "monospace" }}>
        {value}
      </Text>
      <CopyButton value={value} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied" : "Copy"} withinPortal>
            <ActionIcon
              size={18}
              variant="subtle"
              color={copied ? "green" : "gray"}
              onClick={copy}
            >
              {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Metadata row — label / value pair
   ═══════════════════════════════════════════════════════════════════════════ */

function MetaRow({
  label,
  children,
  noBorder,
}: {
  label: string;
  children: React.ReactNode;
  noBorder?: boolean;
}) {
  return (
    <Group
      noWrap
      position="apart"
      py={10}
      sx={(theme) => ({
        borderBottom: noBorder
          ? "none"
          : `1px solid ${
              theme.colorScheme === "dark"
                ? theme.colors.dark[5]
                : theme.colors.gray[1]
            }`,
      })}
    >
      <Text size="xs" color="dimmed" weight={500} sx={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Box sx={{ textAlign: "right" }}>{children}</Box>
    </Group>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Line item card
   ═══════════════════════════════════════════════════════════════════════════ */

function ProductCard({ review }: { review: Review }) {
  const title = review.product_name;
  const imageUrl = review.product_image_url;
  const sku = review.product_sku;
  const lineItem = review.review_line_item;

  return (
    <Paper
      p="sm"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.gray[2]
        }`,
      })}
    >
      <Group>
        {imageUrl ? (
          <Image
            src={imageUrl}
            width={64}
            height={64}
            fit="contain"
            radius="md"
            sx={(theme) => ({
              border: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[4]
                  : theme.colors.gray[2]
              }`,
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[6]
                  : theme.colors.gray[0],
            })}
          />
        ) : (
          <Box
            sx={(theme) => ({
              width: 64,
              height: 64,
              borderRadius: theme.radius.md,
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[6]
                  : theme.colors.gray[1],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            })}
          >
            <IconPackage size={20} color="gray" />
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Text size="sm" weight={600} lineClamp={1}>
            {title || "Untitled product"}
          </Text>
          <Group spacing="xs" mt={4}>
            {sku && (
              <Badge variant="outline" color="gray" size="xs" radius="sm">
                SKU: {sku}
              </Badge>
            )}
            {lineItem?.quantity != null && (
              <Text size="xs" color="dimmed">
                Qty: {lineItem.quantity}
              </Text>
            )}
            {lineItem?.price != null && (
              <Text size="xs" weight={600}>
                ¥{lineItem.price.toLocaleString()}
              </Text>
            )}
          </Group>
        </Box>
      </Group>
    </Paper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Section wrapper
   ═══════════════════════════════════════════════════════════════════════════ */

function Section({
  icon,
  title,
  color,
  count,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  color: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <Paper
      p="lg"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.gray[2]
        }`,
      })}
    >
      <Group spacing="sm" mb="md">
        <ThemeIcon size={28} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Text
          size="xs"
          weight={700}
          transform="uppercase"
          sx={{ letterSpacing: 0.8 }}
        >
          {title}
        </Text>
        {count != null && (
          <Badge variant="light" color="gray" size="xs" radius="xl">
            {count}
          </Badge>
        )}
      </Group>
      {children}
    </Paper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

export const ReviewShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<Review>();
  const { data, isLoading } = queryResult;
  const navigate = useNavigate();
  const { id } = useParams();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const {
    publish,
    unpublish,
    isLoading: isPublishMutating,
  } = usePublishReview();

  const record = data?.data;
  if (!record) return <div />;

  const isPublished = !!record.published_at;

  const handleUnpublish = () =>
    openConfirmModal({
      title: "Unpublish Review",
      children: (
        <Text size="sm">
          This will hide the review from public views. You can publish it again
          later.
        </Text>
      ),
      labels: { confirm: "Unpublish", cancel: "Cancel" },
      confirmProps: { color: "orange" },
      onConfirm: () => unpublish(record.id),
    });

  const currentId = Number(id);
  const ratingHex = STAR_HEX[record.rating] || "#868e96";
  const ratingBgLight = STAR_BG_LIGHT[record.rating] || "transparent";
  const ratingBgDark = STAR_BG_DARK[record.rating] || "transparent";
  const ratingLabel = STAR_LABELS[record.rating] || "";

  const reviewText = [record.title, record.body].filter(Boolean).join("\n\n");

  return (
    <Show isLoading={isLoading} canEdit={false}>
      {/* ── Prev / Next navigation ─────────────────────────────── */}
      <Group position="apart" mb="md">
        <Group spacing="xs">
          <ActionIcon
            variant="light"
            color="gray"
            size="md"
            radius="md"
            onClick={() => navigate(`/reviews/show/${currentId - 1}`)}
          >
            <IconChevronLeft size={16} />
          </ActionIcon>
          <Text size="sm" weight={600} color="dimmed">
            Review #{currentId}
          </Text>
          <ActionIcon
            variant="light"
            color="gray"
            size="md"
            radius="md"
            onClick={() => navigate(`/reviews/show/${currentId + 1}`)}
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <Grid gutter="lg">
        {/* ══════════════════════════════════════════════════════════════════
           MAIN COLUMN
           ══════════════════════════════════════════════════════════════════ */}
        <Grid.Col span={8}>
          <Flex direction="column" gap="md">
            {/* ── Review hero card ──────────────────────────────────────── */}
            <Paper
              radius="md"
              sx={(theme) => ({
                overflow: "hidden",
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              {/* Accent bar at top */}
              <Box
                sx={{
                  height: 4,
                  background: `linear-gradient(90deg, ${ratingHex}, ${ratingHex}88)`,
                }}
              />

              <Box p="lg">
                {/* Rating + label row */}
                <Flex align="center" gap="md" mb="lg">
                  <Box
                    sx={(theme) => ({
                      width: 56,
                      height: 56,
                      borderRadius: theme.radius.md,
                      backgroundColor:
                        theme.colorScheme === "dark"
                          ? ratingBgDark
                          : ratingBgLight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    })}
                  >
                    <Text
                      weight={800}
                      sx={{
                        fontSize: 24,
                        fontFamily: "monospace",
                        color: ratingHex,
                        lineHeight: 1,
                      }}
                    >
                      {record.rating}
                    </Text>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <StarRating rating={record.rating} size={18} />
                    <Text
                      size="xs"
                      weight={600}
                      mt={4}
                      sx={{ color: ratingHex }}
                    >
                      {ratingLabel}
                    </Text>
                  </Box>

                  {record.is_verified && (
                    <Badge
                      variant="light"
                      color="green"
                      size="sm"
                      radius="xl"
                      leftSection={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <IconShieldCheck size={12} />
                        </Box>
                      }
                    >
                      Verified
                    </Badge>
                  )}
                </Flex>

                {/* Title */}
                {record.title && (
                  <Text
                    weight={700}
                    size="lg"
                    mb="xs"
                    sx={{ lineHeight: 1.35 }}
                  >
                    {record.title}
                  </Text>
                )}

                {/* Body — enhanced readability */}
                {record.body ? (
                  <Paper
                    p="md"
                    radius="md"
                    sx={(theme) => ({
                      backgroundColor:
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[7]
                          : theme.colors.gray[0],
                      borderLeft: `3px solid ${ratingHex}`,
                    })}
                  >
                    <Text
                      size="md"
                      sx={(theme) => ({
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.7,
                        color:
                          theme.colorScheme === "dark"
                            ? theme.colors.dark[0]
                            : theme.colors.gray[8],
                      })}
                    >
                      {record.body}
                    </Text>
                  </Paper>
                ) : (
                  <Text size="sm" color="dimmed" italic>
                    No review body provided
                  </Text>
                )}

                {/* Reviewer attribution */}
                <Divider
                  my="md"
                  variant="dashed"
                  sx={(theme) => ({
                    borderColor:
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[5]
                        : theme.colors.gray[2],
                  })}
                />

                <Group spacing="md" noWrap>
                  {/* Avatar initials */}
                  <Box
                    sx={(theme) => ({
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor:
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[5]
                          : theme.colors.gray[2],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    })}
                  >
                    <Text size="sm" weight={700} color="dimmed">
                      {(record.reviewer_name || record.customer_name || "?")
                        .charAt(0)
                        .toUpperCase()}
                    </Text>
                  </Box>

                  <Box sx={{ flexGrow: 1 }}>
                    <Text size="sm" weight={600}>
                      {record.reviewer_name ||
                        record.customer_name ||
                        "Anonymous"}
                    </Text>
                    {record.email && (
                      <Text size="xs" color="dimmed">
                        {record.email}
                      </Text>
                    )}
                  </Box>

                  {record.country_code && (
                    <Box>
                      <Tooltip label={record.country_code} withinPortal>
                        <Group spacing={6} noWrap>
                          <Text size="lg" sx={{ lineHeight: 1 }}>
                            {countryCodeToFlag(record.country_code)}
                          </Text>
                          <Text size="xs" weight={600} color="dimmed">
                            {record.country_code}
                          </Text>
                        </Group>
                      </Tooltip>
                    </Box>
                  )}
                </Group>
              </Box>
            </Paper>

            {/* ── Order details ──────────────────────────────────────── */}
            <Section
              icon={<IconPackage size={14} />}
              title="Order"
              color="grape"
            >
              <Stack spacing={0}>
                <MetaRow label="Order #">
                  <CopyableText value={record.order_number} />
                </MetaRow>
                <MetaRow label="Verified">
                  <Badge
                    variant="light"
                    color={record.is_verified ? "green" : "gray"}
                    size="sm"
                    radius="xl"
                    leftSection={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {record.is_verified ? (
                          <IconShieldCheck size={11} />
                        ) : (
                          <IconShieldOff size={11} />
                        )}
                      </Box>
                    }
                  >
                    {record.is_verified ? "Verified" : "Unverified"}
                  </Badge>
                </MetaRow>
                <MetaRow label="Source" noBorder>
                  <Badge
                    variant="light"
                    color={SOURCE_COLORS[record.source] || "gray"}
                    size="sm"
                    radius="xl"
                    sx={{ fontWeight: 600 }}
                  >
                    {SOURCE_LABELS[record.source] || record.source}
                  </Badge>
                </MetaRow>
              </Stack>
            </Section>

            {/* ── Review request link ──────────────────────────────────── */}
            {record.review_request && (
              <Paper
                p="md"
                radius="md"
                sx={(theme) => ({
                  border: `1px solid ${
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[4]
                      : theme.colors.gray[2]
                  }`,
                  cursor: "pointer",
                  transition: "background-color 150ms ease",
                  "&:hover": {
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[6]
                        : theme.colors.gray[0],
                  },
                })}
                onClick={() =>
                  navigate(`/review_requests/show/${record.review_request!.id}`)
                }
              >
                <Group position="apart" noWrap>
                  <Group spacing="sm" noWrap>
                    <ThemeIcon
                      size={28}
                      radius="md"
                      variant="light"
                      color="blue"
                    >
                      <IconLink size={14} />
                    </ThemeIcon>
                    <Box>
                      <Text
                        size="xs"
                        weight={700}
                        transform="uppercase"
                        sx={{ letterSpacing: 0.8 }}
                      >
                        Linked Review Request
                      </Text>
                      <Group spacing="xs" mt={2}>
                        <Text
                          size="xs"
                          weight={600}
                          sx={{ fontFamily: "monospace" }}
                          color="blue"
                        >
                          #{record.review_request.id}
                        </Text>
                        <Badge
                          variant="light"
                          color={
                            record.review_request.status === "sent"
                              ? "green"
                              : record.review_request.status === "scheduled"
                                ? "blue"
                                : "red"
                          }
                          size="xs"
                          radius="xl"
                        >
                          {record.review_request.status}
                        </Badge>
                        {record.review_request.sent_at && (
                          <Text size="xs" color="dimmed">
                            Sent{" "}
                            <DateField
                              value={record.review_request.sent_at}
                              format="MMM D, YYYY"
                            />
                          </Text>
                        )}
                      </Group>
                    </Box>
                  </Group>
                  <ActionIcon variant="subtle" color="gray" size="sm">
                    <IconExternalLink size={14} />
                  </ActionIcon>
                </Group>
              </Paper>
            )}

            {/* ── Product ─────────────────────────────────────────────── */}
            {(record.product_name || record.review_line_item) && (
              <Section
                icon={<IconPackage size={14} />}
                title="Product"
                color="orange"
              >
                <ProductCard review={record} />
              </Section>
            )}

            {/* ── Media gallery with lightbox ─────────────────────────── */}
            {record.review_media && record.review_media.length > 0 && (
              <Section
                icon={<IconPhoto size={14} />}
                title="Media"
                color="cyan"
                count={record.review_media.length}
              >
                <SimpleGrid cols={4} spacing="sm">
                  {record.review_media.map((media, idx) => {
                    const isOverflow =
                      idx === 3 && record.review_media!.length > 4;
                    const isHidden = idx > 3;
                    if (isHidden) return null;

                    return (
                      <Paper
                        key={media.id}
                        radius="md"
                        sx={(theme) => ({
                          border: `1px solid ${
                            theme.colorScheme === "dark"
                              ? theme.colors.dark[4]
                              : theme.colors.gray[2]
                          }`,
                          overflow: "hidden",
                          cursor: "pointer",
                          position: "relative",
                          transition:
                            "transform 150ms ease, box-shadow 150ms ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: theme.shadows.sm,
                          },
                          "&:hover .media-hover-overlay": {
                            opacity: 1,
                          },
                        })}
                        onClick={() => {
                          if (media.media_type?.startsWith("video")) return;
                          setLightboxUrl(media.url);
                        }}
                      >
                        {media.media_type?.startsWith("video") ? (
                          <Box
                            sx={(theme) => ({
                              width: "100%",
                              aspectRatio: "1",
                              backgroundColor:
                                theme.colorScheme === "dark"
                                  ? theme.colors.dark[6]
                                  : theme.colors.gray[1],
                            })}
                          >
                            <video
                              src={media.url}
                              poster={media.thumb_url}
                              controls
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ position: "relative" }}>
                            <Image
                              src={media.thumb_url || media.url}
                              height={120}
                              fit="cover"
                            />
                            {/* Hover overlay */}
                            <Box
                              className="media-hover-overlay"
                              sx={{
                                position: "absolute",
                                inset: 0,
                                backgroundColor: "rgba(0,0,0,0.4)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0,
                                transition: "opacity 150ms ease",
                              }}
                            >
                              <IconZoomIn size={24} color="white" />
                            </Box>
                            {/* +N more overlay */}
                            {isOverflow && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  backgroundColor: "rgba(0,0,0,0.55)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Text size="lg" weight={700} color="white">
                                  +{record.review_media!.length - 3}
                                </Text>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </SimpleGrid>
              </Section>
            )}
          </Flex>
        </Grid.Col>

        {/* ══════════════════════════════════════════════════════════════════
           SIDEBAR
           ══════════════════════════════════════════════════════════════════ */}
        <Grid.Col span={4}>
          <Flex
            direction="column"
            gap="md"
            sx={{ position: "sticky", top: 16 }}
          >
            {/* ── Action buttons ─────────────────────────────────────── */}
            {isPublished ? (
              <Button
                fullWidth
                size="md"
                variant="light"
                color="orange"
                leftIcon={<IconEyeOff size={18} />}
                loading={isPublishMutating}
                onClick={handleUnpublish}
              >
                Unpublish Review
              </Button>
            ) : (
              <Button
                fullWidth
                size="md"
                variant="light"
                color="green"
                leftIcon={<IconEye size={18} />}
                loading={isPublishMutating}
                onClick={() => publish(record.id)}
              >
                Publish Review
              </Button>
            )}

            {record.review_request && (
              <Button
                fullWidth
                size="md"
                variant="light"
                color="blue"
                leftIcon={<IconExternalLink size={18} />}
                onClick={() =>
                  navigate(`/review_requests/show/${record.review_request!.id}`)
                }
              >
                View Review Request
              </Button>
            )}

            <CopyButton
              value={reviewText || `Review #${record.id}`}
              timeout={2000}
            >
              {({ copied, copy }) => (
                <Button
                  fullWidth
                  size="md"
                  variant="light"
                  color={copied ? "green" : "gray"}
                  leftIcon={
                    copied ? (
                      <IconCheck size={18} />
                    ) : (
                      <IconClipboard size={18} />
                    )
                  }
                  onClick={copy}
                >
                  {copied ? "Copied!" : "Copy Review Text"}
                </Button>
              )}
            </CopyButton>

            {/* ── Review overview card ─────────────────────────────────── */}
            <Paper
              radius="md"
              sx={(theme) => ({
                overflow: "hidden",
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              {/* Rating hero area */}
              <Box
                sx={(theme) => ({
                  padding: theme.spacing.lg,
                  textAlign: "center",
                  backgroundColor:
                    theme.colorScheme === "dark" ? ratingBgDark : ratingBgLight,
                  borderBottom: `1px solid ${
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[4]
                      : theme.colors.gray[2]
                  }`,
                })}
              >
                <Text
                  weight={800}
                  sx={{
                    fontSize: 40,
                    fontFamily: "monospace",
                    color: ratingHex,
                    lineHeight: 1,
                  }}
                >
                  {record.rating}.0
                </Text>
                <Flex justify="center" mt={8} mb={4}>
                  <StarRating rating={record.rating} size={20} />
                </Flex>
                <Text size="xs" weight={600} sx={{ color: ratingHex }}>
                  {ratingLabel}
                </Text>
              </Box>

              {/* Metadata */}
              <Box px="md" py="sm">
                <MetaRow label="Review ID">
                  <Text size="sm" weight={700} sx={{ fontFamily: "monospace" }}>
                    #{record.id}
                  </Text>
                </MetaRow>

                <MetaRow label="Source">
                  <Badge
                    variant="light"
                    color={SOURCE_COLORS[record.source] || "gray"}
                    size="sm"
                    radius="xl"
                    sx={{ fontWeight: 600 }}
                  >
                    {SOURCE_LABELS[record.source] || record.source}
                  </Badge>
                </MetaRow>

                <MetaRow label="Status">
                  <Badge
                    variant="light"
                    color={record.is_verified ? "green" : "gray"}
                    size="sm"
                    radius="xl"
                    leftSection={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {record.is_verified ? (
                          <IconShieldCheck size={11} />
                        ) : (
                          <IconShieldOff size={11} />
                        )}
                      </Box>
                    }
                  >
                    {record.is_verified ? "Verified" : "Unverified"}
                  </Badge>
                </MetaRow>

                <MetaRow label="Publication">
                  <Group spacing={6} noWrap position="right">
                    <Badge
                      variant="light"
                      color={isPublished ? "green" : "gray"}
                      size="sm"
                      radius="xl"
                      leftSection={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {isPublished ? (
                            <IconEye size={11} />
                          ) : (
                            <IconEyeOff size={11} />
                          )}
                        </Box>
                      }
                    >
                      {isPublished ? "Published" : "Unpublished"}
                    </Badge>
                    {isPublished && record.published_at && (
                      <Text size="xs" color="dimmed">
                        <DateField
                          value={record.published_at}
                          format="MMM D, YYYY"
                        />
                      </Text>
                    )}
                  </Group>
                </MetaRow>

                <MetaRow label="Order">
                  {record.order_number ? (
                    <CopyableText value={record.order_number} />
                  ) : (
                    <Text size="sm" color="dimmed">
                      —
                    </Text>
                  )}
                </MetaRow>

                <MetaRow label="Country" noBorder>
                  {record.country_code ? (
                    <Tooltip label={record.country_code} withinPortal>
                      <Group spacing={6} noWrap>
                        <Text size="md" sx={{ lineHeight: 1 }}>
                          {countryCodeToFlag(record.country_code)}
                        </Text>
                        <Text size="sm" weight={600}>
                          {record.country_code}
                        </Text>
                      </Group>
                    </Tooltip>
                  ) : (
                    <Text size="sm" color="dimmed">
                      —
                    </Text>
                  )}
                </MetaRow>
              </Box>
            </Paper>

            {/* ── Reviewer details card (consolidated from B6) ──────── */}
            <Paper
              p="md"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              <Group spacing="sm" mb="sm">
                <ThemeIcon size={22} radius="md" variant="light" color="pink">
                  <IconUser size={12} />
                </ThemeIcon>
                <Text
                  size="xs"
                  weight={700}
                  transform="uppercase"
                  sx={{ letterSpacing: 0.8 }}
                >
                  Reviewer
                </Text>
              </Group>

              <Stack spacing={0}>
                <MetaRow label="Name">
                  <Text size="sm" weight={500}>
                    {record.reviewer_name || "—"}
                  </Text>
                </MetaRow>
                <MetaRow label="Customer">
                  <Text size="sm" weight={500}>
                    {record.customer_name || "—"}
                  </Text>
                </MetaRow>
                <MetaRow label="Email" noBorder>
                  <CopyableText value={record.email} />
                </MetaRow>
              </Stack>
            </Paper>

            {/* ── Timestamps card ──────────────────────────────────────── */}
            <Paper
              p="md"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              <Group spacing="sm" mb="sm">
                <ThemeIcon size={22} radius="md" variant="light" color="gray">
                  <IconCalendar size={12} />
                </ThemeIcon>
                <Text
                  size="xs"
                  weight={700}
                  transform="uppercase"
                  sx={{ letterSpacing: 0.8 }}
                >
                  Timestamps
                </Text>
              </Group>

              <Flex direction="column" gap={8}>
                <Group position="apart">
                  <Text size="xs" color="dimmed">
                    Created
                  </Text>
                  <Text size="xs" weight={500}>
                    <DateField
                      value={record.created_at}
                      format="MMM D, YYYY HH:mm"
                    />
                  </Text>
                </Group>
                <Divider variant="dashed" />
                <Group position="apart">
                  <Text size="xs" color="dimmed">
                    Updated
                  </Text>
                  <Text size="xs" weight={500}>
                    <DateField
                      value={record.updated_at}
                      format="MMM D, YYYY HH:mm"
                    />
                  </Text>
                </Group>
              </Flex>
            </Paper>
          </Flex>
        </Grid.Col>
      </Grid>

      {/* ── Lightbox modal ───────────────────────────────────────── */}
      <Modal
        opened={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        size="xl"
        padding={0}
        radius="md"
        withCloseButton
        centered
        overlayOpacity={0.7}
        overlayBlur={3}
      >
        {lightboxUrl && (
          <Image src={lightboxUrl} fit="contain" sx={{ maxHeight: "80vh" }} />
        )}
      </Modal>
    </Show>
  );
};
